import { zodResolver } from '@hookform/resolvers/zod';
import type { I18n } from '@lingui/core';
import { useLingui } from '@lingui/react/macro';
import { CircleAlert, CloudUpload, Download, FileCheck2, FileText, X } from 'lucide-react';
import { useEffect, useId, useRef, useState, type DragEvent, type ReactNode } from 'react';
import { FormProvider, useForm, type DefaultValues, type FieldValues, type UseFormReturn } from 'react-hook-form';
import type { z } from 'zod';

import { notifyApiError, useNotifier, type Notice } from '@/shared/data';
// Direto do arquivo: o índice de `shared/flow` importa `@/shared/patterns` e fecharia um ciclo.
import { FlowPanelTrail, type FlowPanelStep } from '@/shared/flow/flow-panel';
import { ApiError, downloadBlob } from '@/shared/http';
import { useIsMobile } from '@/shared/mobile';
import { Alert } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import { cn } from '@/shared/ui/cn';
import { Sheet, SheetBody, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/shared/ui/sheet';

import type { ColumnSpec } from './columns';
import { DataView } from './data-view';

/** Linhas do resultado de uma importação ("Erros de leitura do arquivo", "Linhas rejeitadas"). */
export interface FileImportSection {
  readonly id: string;
  readonly title: string;
  readonly columns: readonly ColumnSpec[];
  readonly rows: readonly Record<string, unknown>[];
  /** Campo ou função com a identidade da linha; padrão, a posição. */
  readonly rowKey?: string | ((row: Record<string, unknown>) => string);
}

/** O que mostrar depois de validar ou enviar: resumo, totais e linhas (o módulo mapeia o fio). */
export interface FileImportReport {
  readonly tone: 'success' | 'warning' | 'error';
  readonly title: string;
  readonly description?: string;
  readonly totals?: readonly { readonly label: string; readonly value: string | number }[];
  readonly sections?: readonly FileImportSection[];
}

export interface FileImportTemplate {
  readonly label?: string;
  /** Gera ou baixa o template; o sheet entrega pelo `downloadBlob` da F1a. */
  readonly get: (signal: AbortSignal) => Promise<{ readonly blob: Blob; readonly fileName: string }> | { readonly blob: Blob; readonly fileName: string };
}

export interface FileImportStep<E, R> {
  /** Chamada do módulo (multipart, timeout, query); recebe o arquivo e os campos extras validados. */
  readonly run: (input: E & { readonly file: File }, signal: AbortSignal) => Promise<R>;
  /** Resultado por linha no sheet; ausente, só o aviso (`feedback`). */
  readonly report?: (result: R, i18n: I18n) => FileImportReport;
  /** Aviso depois do sucesso (tom e texto do legado); ausente, nenhum. */
  readonly feedback?: (result: R, i18n: I18n) => Notice | null;
  readonly label?: string;
  readonly errorTitle?: (error: ApiError, i18n: I18n) => string;
}

export interface FileImportSheetProps<E extends FieldValues, V, R> {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly title: ReactNode;
  readonly description?: ReactNode;
  /** `accept` do input, ex.: `.csv` ou `.rem,.txt`. Arquivo arrastado fora da lista é recusado. */
  readonly accept: string;
  /** Formato em uma frase, embaixo da área de envio. */
  readonly hint?: ReactNode;
  readonly template?: FileImportTemplate;
  /** Campos extras (nome, "Sobrescrever já liquidadas"…) no formulário do sheet. */
  readonly fields?: {
    readonly schema: z.ZodType<E, E>;
    readonly defaults: E;
    readonly render: (form: UseFormReturn<E>) => ReactNode;
    /** Ao escolher o arquivo (o legado preenche `name` com o nome do arquivo). */
    readonly onFile?: (file: File, form: UseFormReturn<E>) => void;
  };
  /** Validação prévia (ex.: `POST /FileInstruction/Validate`), quando o endpoint existe. */
  readonly validate?: FileImportStep<E, V>;
  /** Envio (ex.: `POST /FileInstruction`). */
  readonly submit: FileImportStep<E, R>;
  /**
   * Envio concluído: sem relatório, o sheet fecha e chama (o legado da carteira fecha e recarrega a
   * lista). Com o sheet já fechado durante o envio, chama sempre (`UploadFileInstructionContainer.tsx:33-43`).
   */
  readonly onSubmitted?: (result: R) => void;
}

type Phase = 'idle' | 'validating' | 'submitting';
type TrailStep = 'file' | 'validate' | 'submit' | 'report';

function matchesAccept(file: File, accept: string): boolean {
  const rules = accept
    .split(',')
    .map((rule) => rule.trim().toLowerCase())
    .filter(Boolean);
  if (rules.length === 0) return true;
  const name = file.name.toLowerCase();
  const type = file.type.toLowerCase();
  return rules.some((rule) =>
    rule.startsWith('.') ? name.endsWith(rule) : rule.endsWith('/*') ? type.startsWith(rule.slice(0, -1)) : type === rule,
  );
}

function formatSize(i18n: I18n, bytes: number) {
  const mb = bytes / (1024 * 1024);
  return mb >= 1
    ? `${new Intl.NumberFormat(i18n.locale, { maximumFractionDigits: 1 }).format(mb)} MB`
    : `${new Intl.NumberFormat(i18n.locale, { maximumFractionDigits: 0 }).format(Math.max(1, bytes / 1024))} KB`;
}

const PAGE_SIZE = 10;

function ReportSection({ section }: { readonly section: FileImportSection }) {
  const [page, setPage] = useState({ pageIndex: 0, pageSize: PAGE_SIZE });
  const start = page.pageIndex * page.pageSize;
  const items = section.rows.slice(start, start + page.pageSize).map((row, index) => ({ __index: String(start + index), ...row }));
  const rowKey = section.rowKey;
  return (
    <div className="flex min-w-0 flex-col gap-2" data-slot="file-import-section">
      <h4 className="text-sm font-semibold text-foreground">{section.title}</h4>
      <DataView<Record<string, unknown>>
        label={section.title}
        columns={section.columns}
        page={{ items, total: section.rows.length, pageCount: Math.max(1, Math.ceil(section.rows.length / page.pageSize)) }}
        rowKey={typeof rowKey === 'function' ? rowKey : (rowKey ?? '__index')}
        fit="content"
        {...(section.rows.length > PAGE_SIZE ? { pagination: { ...page, onChange: setPage } } : {})}
      />
    </div>
  );
}

function ReportView({ report }: { readonly report: FileImportReport }) {
  const tone = report.tone === 'success' ? 'ok' : report.tone === 'warning' ? 'warn' : 'err';
  return (
    <div className="flex min-w-0 flex-col gap-4" data-slot="file-import-report" data-tone={report.tone}>
      <Alert tone={tone} title={report.title} role={report.tone === 'error' ? 'alert' : 'status'}>
        {report.description}
      </Alert>
      {report.totals && report.totals.length > 0 ? (
        <dl className="grid grid-cols-3 gap-2 max-[360px]:grid-cols-1">
          {report.totals.map((total) => (
            <div key={total.label} className="flex flex-col gap-0.5 rounded-card border border-border bg-card px-3 py-2">
              <dt className="text-xs text-muted-foreground">{total.label}</dt>
              <dd className="font-mono text-lg font-semibold tabular-nums text-foreground">{total.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}
      {(report.sections ?? [])
        .filter((section) => section.rows.length > 0)
        .map((section) => (
          <ReportSection key={section.id} section={section} />
        ))}
    </div>
  );
}

function FileDrop({
  accept,
  file,
  disabled,
  onFile,
}: {
  readonly accept: string;
  readonly file: File | null;
  readonly disabled: boolean;
  readonly onFile: (file: File | null) => void;
}) {
  const { i18n, t } = useLingui();
  const inputId = useId();
  const [over, setOver] = useState(false);
  const [rejected, setRejected] = useState<string | null>(null);
  const pick = (next: File | null | undefined) => {
    if (!next) return;
    if (!matchesAccept(next, accept)) {
      setRejected(next.name);
      return;
    }
    setRejected(null);
    onFile(next);
  };
  const drop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setOver(false);
    if (!disabled) pick(event.dataTransfer.files.item(0));
  };
  return (
    <div className="flex flex-col gap-2" data-slot="file-import-drop">
      <div
        onDragOver={(event) => {
          event.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={drop}
        className={cn(
          'flex flex-col items-center gap-2 rounded-card border-[1.5px] border-dashed border-accent-line bg-accent-wash px-4 py-6 text-center transition-colors duration-(--lume-dur-instant)',
          over && 'border-accent',
        )}
      >
        <CloudUpload aria-hidden className="size-8 text-accent-text" />
        <label htmlFor={inputId} className="cursor-pointer text-sm font-medium text-foreground">
          {t`Arraste o arquivo ou`} <span className="text-accent-text underline underline-offset-4">{t`escolha no computador`}</span>
        </label>
        <input
          id={inputId}
          type="file"
          accept={accept}
          aria-label={t`Arquivo`}
          className="sr-only"
          disabled={disabled}
          onChange={(event) => {
            const next = event.target.files?.item(0);
            event.target.value = '';
            pick(next);
          }}
        />
        <span className="text-xs text-foreground">{t`Formatos aceitos: ${accept}`}</span>
      </div>
      {rejected ? (
        <p role="alert" className="flex items-center gap-2 text-sm text-err">
          <CircleAlert aria-hidden className="size-4" />
          {t`${rejected} não está num formato aceito (${accept}).`}
        </p>
      ) : null}
      {file ? (
        <div className="flex min-w-0 items-center gap-3 rounded-card border border-border bg-card px-3 py-2" data-slot="file-import-chosen">
          <FileText aria-hidden className="size-5 shrink-0 text-muted-foreground" />
          <div className="flex min-w-0 flex-1 flex-col">
            <span className="truncate text-sm font-medium text-foreground">{file.name}</span>
            <span className="text-xs text-muted-foreground">{formatSize(i18n, file.size)}</span>
          </div>
          <Button type="button" variant="ghost" size="icon" aria-label={t`Remover ${file.name}`} onClick={() => onFile(null)} disabled={disabled}>
            <X aria-hidden />
          </Button>
        </div>
      ) : null}
    </div>
  );
}

interface BodyProps<E extends FieldValues, V, R> extends FileImportSheetProps<E, V, R> {
  readonly mobile: boolean;
  /** Envio de uma abertura anterior ainda em curso: esta abertura não envia de novo. */
  readonly submitPending: boolean;
  readonly onSubmitPending: (pending: boolean) => void;
}

function FileImportBody<E extends FieldValues, V, R>(props: BodyProps<E, V, R>) {
  const { i18n, t } = useLingui();
  const notifier = useNotifier();
  const formId = useId();
  const [file, setFile] = useState<File | null>(null);
  const [phase, setPhase] = useState<Phase>('idle');
  const [report, setReport] = useState<{ readonly step: 'validate' | 'submit'; readonly value: FileImportReport } | null>(null);
  // Só para o trilho: a validação deste arquivo terminou sem relatório de erro (o envio segue liberado sem ela).
  const [validated, setValidated] = useState(false);
  const [templateBusy, setTemplateBusy] = useState(false);
  // Só validação e template: o envio não é idempotente e segue até o fim mesmo com o sheet fechado (R1).
  const aborter = useRef<AbortController | null>(null);
  const templateAborter = useRef<AbortController | null>(null);
  const openRef = useRef(props.open);
  const latest = useRef(props);
  const form = useForm<E, unknown, E>({
    ...(props.fields ? { resolver: zodResolver(props.fields.schema) } : {}),
    defaultValues: (props.fields?.defaults ?? {}) as DefaultValues<E>,
    mode: 'onTouched',
  });
  const submitting = phase === 'submitting' || props.submitPending;
  const busy = phase !== 'idle' || props.submitPending;
  const done = report?.step === 'submit';
  useEffect(() => {
    latest.current = props;
  });
  useEffect(() => {
    openRef.current = props.open;
    if (!props.open) {
      aborter.current?.abort();
      templateAborter.current?.abort();
    }
  }, [props.open]);
  useEffect(
    () => () => {
      openRef.current = false;
      aborter.current?.abort();
      templateAborter.current?.abort();
    },
    [],
  );

  const run = async <T,>(which: 'validate' | 'submit', step: FileImportStep<E, T>) => {
    if (!file) return;
    await form.handleSubmit(async (values) => {
      const sending = which === 'submit';
      const controller = new AbortController();
      if (!sending) {
        aborter.current?.abort();
        aborter.current = controller;
      }
      setPhase(sending ? 'submitting' : 'validating');
      if (sending) props.onSubmitPending(true);
      setReport(null);
      if (!sending) setValidated(false);
      try {
        const result = await step.run({ ...values, file }, controller.signal);
        const notice = step.feedback?.(result, i18n);
        if (notice) notifier.notify(notice);
        const current = latest.current;
        const value = openRef.current && step.report ? step.report(result, i18n) : null;
        if (!sending) setValidated(value?.tone !== 'error');
        if (sending && !openRef.current) current.onSubmitted?.(result as unknown as R);
        else if (!openRef.current) return;
        else if (value) setReport({ step: which, value });
        else if (sending) {
          current.onSubmitted?.(result as unknown as R);
          current.onOpenChange(false);
        }
      } catch (error) {
        if (!controller.signal.aborted) notifyApiError(notifier, ApiError.from(error), step.errorTitle?.(ApiError.from(error), i18n));
      } finally {
        if (aborter.current === controller) aborter.current = null;
        if (sending) latest.current.onSubmitPending(false);
        setPhase('idle');
      }
    })();
  };

  const choose = (next: File | null) => {
    setFile(next);
    setReport(null);
    setValidated(false);
    if (next) props.fields?.onFile?.(next, form);
  };

  const downloadTemplate = async () => {
    if (!props.template) return;
    templateAborter.current?.abort();
    const controller = new AbortController();
    templateAborter.current = controller;
    setTemplateBusy(true);
    try {
      const { blob, fileName } = await props.template.get(controller.signal);
      if (!controller.signal.aborted) downloadBlob(blob, fileName);
    } catch (error) {
      if (!controller.signal.aborted) notifyApiError(notifier, ApiError.from(error));
    } finally {
      if (templateAborter.current === controller) templateAborter.current = null;
      setTemplateBusy(false);
    }
  };

  const restart = () => {
    setFile(null);
    setReport(null);
    setValidated(false);
    form.reset(props.fields?.defaults);
  };

  const trailSteps: (FlowPanelStep & { readonly id: TrailStep })[] = [
    { id: 'file', title: t`Arquivo` },
    ...(props.validate ? [{ id: 'validate' as const, title: t`Validar` }] : []),
    { id: 'submit', title: t`Enviar` },
    { id: 'report', title: t`Relatório` },
  ];
  const trailCurrent: TrailStep = done
    ? 'report'
    : submitting
      ? 'submit'
      : !file
        ? 'file'
        : props.validate && !validated
          ? 'validate'
          : 'submit';
  const currentIndex = trailSteps.findIndex((step) => step.id === trailCurrent);
  // Validar pulado não aparece como concluído: o envio sem validar continua permitido.
  const trailCompleted = trailSteps.slice(0, currentIndex).filter((step) => step.id !== 'validate' || validated).map((step) => step.id);

  const size = props.mobile ? 'lg' : 'default';
  const footer = done ? (
    <>
      <Button type="button" variant="ghost" size={size} onClick={() => props.onOpenChange(false)} className="max-sm:w-full">
        {t`Fechar`}
      </Button>
      <Button type="button" variant="secondary" size={size} onClick={restart} className="max-sm:w-full">
        {t`Importar outro arquivo`}
      </Button>
    </>
  ) : (
    <>
      <Button type="button" variant="ghost" size={size} onClick={() => props.onOpenChange(false)} className="max-sm:w-full">
        {t`Fechar`}
      </Button>
      {props.validate ? (
        <Button
          type="button"
          variant="secondary"
          size={size}
          disabled={!file || busy}
          loading={phase === 'validating'}
          onClick={() => void run('validate', props.validate as FileImportStep<E, V>)}
          className="max-sm:w-full"
        >
          <FileCheck2 aria-hidden />
          {props.validate.label ?? t`Validar arquivo`}
        </Button>
      ) : null}
      <Button type="submit" form={formId} size={size} disabled={!file || busy} loading={submitting} className="max-sm:w-full">
        {props.submit.label ?? t`Confirmar`}
      </Button>
    </>
  );

  return (
    <>
      <SheetHeader className={cn(props.mobile && 'pt-[calc(env(safe-area-inset-top,0px)+1rem)]')}>
        <SheetTitle>{props.title}</SheetTitle>
        {props.description ? <SheetDescription>{props.description}</SheetDescription> : <SheetDescription className="sr-only">{props.title}</SheetDescription>}
        <FlowPanelTrail steps={trailSteps} current={trailCurrent} completed={trailCompleted} className="mt-1" />
      </SheetHeader>
      <SheetBody>
        <FormProvider {...form}>
          <form
            id={formId}
            noValidate
            aria-busy={busy}
            onSubmit={(event) => {
              event.preventDefault();
              event.stopPropagation();
              void run('submit', props.submit);
            }}
            className="flex flex-col gap-4"
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-base font-semibold text-foreground">{t`Arquivo`}</h3>
              {props.template ? (
                <Button type="button" variant="secondary" size="sm" onClick={() => void downloadTemplate()} loading={templateBusy}>
                  <Download aria-hidden />
                  {props.template.label ?? t`Baixar template`}
                </Button>
              ) : null}
            </div>
            <FileDrop accept={props.accept} file={file} disabled={busy} onFile={choose} />
            {props.hint ? <p className="text-xs text-muted-foreground">{props.hint}</p> : null}
            {props.fields ? <fieldset disabled={busy} className="flex flex-col gap-4">{props.fields.render(form)}</fieldset> : null}
            <div aria-live="polite" className="min-w-0">
              {phase === 'validating' ? <p className="text-sm text-muted-foreground">{t`Validando o arquivo…`}</p> : null}
              {submitting ? <p className="text-sm text-muted-foreground">{t`Enviando o arquivo…`}</p> : null}
              {report ? <ReportView report={report.value} /> : null}
            </div>
          </form>
        </FormProvider>
      </SheetBody>
      <SheetFooter className={cn(props.mobile && 'pb-[calc(env(safe-area-inset-bottom,0px)+0.75rem)]')}>{footer}</SheetFooter>
    </>
  );
}

/**
 * Importação de arquivo (P18, F3a): escolher ou arrastar → validar (quando há endpoint) → resultado por
 * linha → enviar, com template para baixar e o trilho "Arquivo · Validar · Enviar · Relatório" no topo (U53). Genérico: o módulo passa o fio, os campos extras e o
 * mapeamento do resultado. Lateral na web, tela cheia no iPhone. Fechar cancela validação e template;
 * o envio segue e entrega aviso e `onSubmitted` mesmo com o sheet fechado.
 */
export function FileImportSheet<E extends FieldValues = Record<string, never>, V = unknown, R = unknown>(props: FileImportSheetProps<E, V, R>) {
  const isMobile = useIsMobile();
  // Cada abertura recomeça do zero; o corpo continua montado na saída para a animação (U38).
  const [session, setSession] = useState(0);
  const [wasOpen, setWasOpen] = useState(props.open);
  const [submitPending, setSubmitPending] = useState(false);
  if (props.open !== wasOpen) {
    setWasOpen(props.open);
    if (props.open) setSession((value) => value + 1);
  }
  return (
    <Sheet open={props.open} onOpenChange={props.onOpenChange}>
      <SheetContent data-slot="file-import-sheet" size="md" showClose={!isMobile} className={cn(isMobile && 'max-w-none border-l-0')}>
        <FileImportBody key={session} {...props} mobile={isMobile} submitPending={submitPending} onSubmitPending={setSubmitPending} />
      </SheetContent>
    </Sheet>
  );
}
